// TOEFL iBT Mock 5 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// NOTE: Mock Test 5 in the PDF has a single Module 2, so `upper` and `lower`
// currently point to the same content. Replace `lower` with easier material
// if you want true adaptive routing.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "Pleistocene Megafauna",
      text: "During the Pleistocene epoch, massive mammals known as megafauna roamed the Northern Hemisphere. These [[creatures]], [[including]] the woolly mammoth, [[developed]] specialized [[adaptations]] to [[survive]] in [[harsh]], freezing [[climates]]. Their [[thick]] coats of hair provided [[excellent]] insulation [[against]] the severe [[cold]]. Furthermore, a [[dense]] layer of fat stored [[beneath]] their [[skin]] served [[both]] as a thermal barrier and a vital energy reserve during long, dark winters when food was scarce. As global temperatures eventually began to rise, these massive animals faced significant environmental changes that ultimately contributed to their widespread extinction.",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Biology Advising Sign-Up",
      text: `Biology Advising Sign-Up: Students planning to take Biology 210 next semester must meet with an academic adviser before registration opens. Appointment slots will be available on the department website starting Wednesday at 9:00 a.m. Bring a copy of your current degree plan to the meeting. Students who have already completed Biology 110 do not need instructor permission, but they still need advising approval. Walk-in advising will not be offered this semester.`,
      questions: [
        {
          question: "What is the main purpose of the announcement?",
          options: [
            "To cancel Biology 210 for next semester",
            "To tell students how to prepare for Biology 210 registration",
            "To announce new walk-in advising hours",
            "To explain the content of Biology 110",
          ],
          correct: 1,
        },
        {
          question: "What should students bring to the advising meeting?",
          options: [
            "A Biology 110 textbook",
            "Instructor permission forms",
            "A copy of their current degree plan",
            "A printed registration receipt",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred about students who completed Biology 110?",
          options: [
            "They can register without any approval.",
            "They must attend walk-in advising.",
            "They are not allowed to take Biology 210.",
            "They still must meet with an adviser before registration.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Fitness Club Membership Policy",
      text: `Pinewood Fitness Club - Membership Freeze and Cancellation Policy

Members may freeze their membership for a minimum of one month and a maximum of three months per calendar year. A freeze requires a written request submitted at least seven days before the next billing date, and a $10 monthly hold fee applies.

Cancellations require thirty days' written notice; payments already processed are not refundable. Members who relocate more than 50 miles from the club may cancel without notice by providing proof of a new address. Annual members who cancel early will be charged a fee equal to two months of dues. Requests can be delivered at the front desk or emailed to membership@pinewoodfit.com. Verbal requests cannot be accepted.`,
      questions: [
        {
          question: "What is required to freeze a membership?",
          options: [
            "Proof of a new address",
            "A written request at least seven days before billing",
            "A phone call to the manager",
            "Payment of two months of dues",
          ],
          correct: 1,
        },
        {
          question: "Which member could cancel without giving thirty days' notice?",
          options: [
            "A member who has paid for the whole year",
            "A member who is unhappy with the equipment",
            "A member who telephones the front desk",
            "A member who moved 80 miles away and has proof",
          ],
          correct: 3,
        },
        {
          question: "In the policy, the word \"relocate\" is closest in meaning to",
          options: [
            "move to a new place",
            "change a schedule",
            "start a business",
            "visit a friend",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "The Studio System in the Golden Age",
      text: `From roughly 1930 to 1948, the American film industry was defined by the studio system, a business model that allowed a few major corporations to control the entire movie-making process. These studios, often referred to as the 'Big Five,' operated as an oligopoly, effectively eliminating significant competition. Central to their power was a strategy known as vertical integration. [A] Unlike modern studios that primarily focus on production and distribution, these historic giants also owned the theater chains where their films were shown. [B] By controlling exhibition, they guaranteed an audience for their products, regardless of critical reception. [C]

To maximize efficiency, studios employed the controversial practice of block booking. Independent theater owners who wanted to screen a studio's popular 'A-list' movies featuring famous stars were required to rent a package of films that included several lower-budget 'B-movies.' [D] Consequently, studios could maintain a steady stream of production without the financial risk usually associated with artistic ventures. This ensured that production facilities remained active and profitable year-round.

The system also extended to the talent, creating what was known as the 'star system.' Actors were bound by stringent long-term contracts that dictated not only their professional roles but often their personal lives. Studios invested heavily in creating public personas for their stars, sometimes inventing backstories or changing names to suit market trends. While this provided actors with job security and training, it left them with little creative control over their careers. The era eventually ended following a 1948 Supreme Court ruling that forced studios to sell their theater holdings, dismantling the vertical integration that had sustained the Golden Age.`,
      questions: [
        {
          question: "According to the passage, how did vertical integration benefit the major studios?",
          options: [
            "It allowed them to avoid signing actors to long-term contracts.",
            "It ensured that their films would be screened in theaters they owned.",
            "It encouraged independent theater owners to produce their own movies.",
            "It eliminated the need to produce lower-budget B-movies.",
          ],
          correct: 1,
        },
        {
          question: "The word \"stringent\" in the passage is closest in meaning to",
          options: [
            "strict",
            "generous",
            "brief",
            "confusing",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred about independent theater owners during the studio system era?",
          options: [
            "They frequently refused to show A-list movies to avoid paying high rental fees.",
            "They had full freedom to select only the highest quality films for their audiences.",
            "They were often forced to show films they did not want in order to get popular titles.",
            "They were the primary financial backers of the major studios.",
          ],
          correct: 2,
        },
        {
          question: "All of the following are mentioned as characteristics of the 'star system' EXCEPT:",
          options: [
            "Studios sometimes changed the names of actors.",
            "Actors were given significant control over the scripts they performed.",
            "Contracts often regulated the personal lives of actors.",
            "The system provided actors with training and job stability.",
          ],
          correct: 1,
        },
        {
          question: "Where would the following sentence best fit in paragraph 2? \"This bundling strategy meant that for every hit film an exhibitor wanted, they had to rent several less desirable productions.\"",
          options: [
            "Choice A",
            "Choice B",
            "Choice C",
            "Choice D",
          ],
          correct: 3,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "Inflation",
      text: "Inflation describes a general rise in the prices of goods and services over a period of time. [[When]] it occurs, [[each]] unit of [[money]] buys [[fewer]] products [[than]] before, so [[the]] purchasing [[power]] of households [[gradually]] falls. [[Economists]] often [[link]] inflation to [[strong]] consumer [[demand]], higher [[production]] costs, or an [[increase]] in the [[supply]] of money. Central banks usually respond by raising interest rates, which makes borrowing more expensive and encourages people to spend less. A small and steady increase in prices is generally considered healthy because it supports growth and investment. However, rapid inflation can damage savings, create uncertainty for businesses, and place heavy pressure on families with low incomes.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Route 42 Service Advisory",
      text: `Metro Transit Service Advisory - Route 42

Due to a water main repair on Elm Avenue, Route 42 buses will be detoured from Friday, May 2, at 10:00 p.m. until Monday, May 5, at 5:00 a.m. Buses will not stop at Elm & 3rd, Elm & 5th, or Elm & 7th. Instead, riders may board at temporary stops on Birch Street, one block north. Expect delays of 10 to 15 minutes.

Route 18 will operate on its normal schedule and also serves the Elm Avenue area, though it does not reach the hospital. Passengers who use wheelchairs or other mobility devices can call 555-0188 to arrange alternate transportation. Real-time updates are posted on the Metro Transit app.`,
      questions: [
        {
          question: "When is Route 42 expected to return to normal service?",
          options: [
            "Friday, May 2, at 10:00 p.m.",
            "Saturday morning",
            "Monday, May 5, at 5:00 a.m.",
            "Tuesday, May 6",
          ],
          correct: 2,
        },
        {
          question: "What should a rider who usually boards at Elm & 5th do during the detour?",
          options: [
            "Take Route 18 instead",
            "Board at a temporary stop on Birch Street",
            "Walk to the hospital",
            "Wait for a delayed bus at Elm & 5th",
          ],
          correct: 1,
        },
        {
          question: "Why might Route 18 be a poor choice for someone going to the hospital?",
          options: [
            "It runs only on weekends.",
            "It is always delayed by 15 minutes.",
            "It does not stop on Elm Avenue.",
            "It does not go to the hospital.",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Library Assistant Job Posting",
      text: `Part-Time Library Assistant - Eastgate Branch Library

Hours: 15 to 20 per week, including some evenings and Saturdays. Pay: $17.50 per hour.

Responsibilities: Check materials in and out, reshelve books, help patrons use computers and the self-checkout machines, and assist with children's programs.

Requirements: High school diploma, friendly manner, and basic computer skills. Experience in customer service is preferred but not required. Bilingual applicants (English and Spanish) are especially welcome. Applicants must be at least 18 years old.

To apply, submit a resume and a short cover letter through the county jobs website by November 21. Interviews will take place the week of December 1. The selected candidate will begin with paid training in January.`,
      questions: [
        {
          question: "All of the following are required EXCEPT",
          options: [
            "previous work in customer service",
            "a high school diploma",
            "basic computer skills",
            "being at least 18 years old",
          ],
          correct: 0,
        },
        {
          question: "How should interested people apply?",
          options: [
            "Visit the Eastgate Branch in person",
            "Call the library on December 1",
            "Send a resume and cover letter through a website",
            "Email the children's program coordinator",
          ],
          correct: 2,
        },
        {
          question: "In the posting, the word \"preferred\" is closest in meaning to",
          options: [
            "required",
            "considered an advantage",
            "expected in every case",
            "paid extra",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "Intelligence in Corvids",
      text: `For much of the twentieth century, many scientists assumed that complex thinking was largely confined to primates. Birds were considered creatures driven by instinct, whose small brains limited their intelligence. Research over the past few decades has overturned this assumption, particularly with regard to corvids, the family that includes crows, ravens, and jays.

New Caledonian crows are celebrated for their use of tools. In the wild, they fashion hooks from twigs and cut strips from the barbed edges of leaves, which they use to extract insect larvae from crevices. [A] Laboratory experiments have shown that these birds can even bend a straight piece of wire into a hook to retrieve food from a narrow tube, a task that requires linking a problem with an appropriate solution. [B] Because tool designs vary between regions, some researchers suspect that the techniques are partly passed on through social learning.

Corvids also display remarkable planning and memory. Western scrub-jays hide food in many locations, a behavior known as caching, and later retrieve it. [C] If other birds observed them while caching, the jays frequently return to move the food to new spots, behavior that suggests they can anticipate the intentions of potential thieves. In addition, experiments inspired by one of Aesop's fables have shown that rooks drop stones into a water-filled tube to raise the level and reach a floating treat.

Scientists nevertheless urge caution in interpreting such results. Impressive performance on a task does not necessarily show that an animal understands cause and effect in the way humans do. [D] Researchers therefore design experiments with careful controls to rule out simpler explanations, such as trial-and-error learning. Still, the evidence suggests that high intelligence has evolved independently in birds and mammals, whose brains are organized quite differently.`,
      questions: [
        {
          question: "The word \"fashion\" in the passage is closest in meaning to",
          options: [
            "admire",
            "discard",
            "borrow",
            "make",
          ],
          correct: 3,
        },
        {
          question: "According to paragraph 2, why do some researchers think tool-making techniques are partly learned socially?",
          options: [
            "Tool designs differ from one region to another.",
            "Young crows are taught in laboratories.",
            "Crows cannot make tools without wire.",
            "Only ravens use tools in the wild.",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred from paragraph 3 about scrub-jays that move their hidden food?",
          options: [
            "They forget where they first hid it.",
            "They hide food only when no birds are nearby.",
            "They seem to expect that observers might steal it.",
            "They prefer to eat food that other birds have hidden.",
          ],
          correct: 2,
        },
        {
          question: "Why does the author mention \"trial-and-error learning\" in paragraph 4?",
          options: [
            "To prove that corvids cannot solve problems",
            "To give an example of a simpler explanation researchers must rule out",
            "To describe a method used in laboratory wire experiments",
            "To contrast bird intelligence with human intelligence",
          ],
          correct: 1,
        },
        {
          question: "Where would the following sentence best fit? \"Such flexibility is striking because the birds had presumably never encountered wire in their natural environment.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 1,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: module2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}